// controls.js
// Usage from script.js:
//   import { initControls } from "./controls.js";
//   initControls({ vehicle, CANNON, joystick:"#joystick" });

export function initControls({
  vehicle,
  CANNON,
  joystick = "#joystick",
  maxSteerVal = Math.PI / 8,
  maxForce = 30,
  brakeForce = 100,
} = {}) {
  if (!vehicle?.chassisBody) {
    console.warn("initControls: vehicle missing or not ready");
    return { destroy() {} };
  }

  // ---------- Keyboard (desktop) ----------
  const onKeyDown = (e) => {
    switch (e.key) {
      case "ArrowUp":
        vehicle.setWheelForce(maxForce, 0);
        vehicle.setWheelForce(maxForce, 1);
        break;
      case "ArrowDown": {
        const v = vehicle.chassisBody.velocity;
        const movingFwd = v.z < -0.1 || v.x < -0.1;
        if (movingFwd) {
          vehicle.setWheelForce(-brakeForce, 0);
          vehicle.setWheelForce(-brakeForce, 1);
        } else {
          vehicle.setWheelForce(-maxForce / 2, 0);
          vehicle.setWheelForce(-maxForce / 2, 1);
        }
        break;
      }
      case "ArrowLeft":
        vehicle.setSteeringValue(maxSteerVal, 0);
        vehicle.setSteeringValue(maxSteerVal, 1);
        break;
      case "ArrowRight":
        vehicle.setSteeringValue(-maxSteerVal, 0);
        vehicle.setSteeringValue(-maxSteerVal, 1);
        break;
    }
  };
  const onKeyUp = (e) => {
    switch (e.key) {
      case "ArrowUp":
      case "ArrowDown":
        vehicle.setWheelForce(0, 0);
        vehicle.setWheelForce(0, 1);
        break;
      case "ArrowLeft":
      case "ArrowRight":
        vehicle.setSteeringValue(0, 0);
        vehicle.setSteeringValue(0, 1);
        break;
    }
  };
  document.addEventListener("keydown", onKeyDown);
  document.addEventListener("keyup", onKeyUp);

  // ---------- Joystick (phones/tablets) ----------
  const joy =
    typeof joystick === "string" ? document.querySelector(joystick) : joystick;
  const stick = joy?.querySelector?.(".stick");
  let destroyJoystick = () => {};

  if (joy && stick) {
    // iOS requires this to make preventDefault work for touch
    if (!joy.style.touchAction) joy.style.touchAction = "none";

    // knobs
    const DEADZONE = 0.12,
      STEER_GAIN = 1.2,
      LERP = 0.2,
      REVERSE_FACTOR = 0.7;
    const INVERT_X = true,
      INVERT_Y = true;

    let rect, cx, cy, maxDist;
    let jx = 0,
      jy = 0;
    let steerSm = 0,
      forceSm = 0;
    let active = false,
      activePointerId = null;
    let braking = false,
      brakeUntil = 0;
    let rafId = 0;

    const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
    const lerp = (a, b, t) => a + (b - a) * t;

    function recalc() {
      rect = joy.getBoundingClientRect();
      cx = rect.left + rect.width / 2;
      cy = rect.top + rect.height / 2;
      const srect = stick.getBoundingClientRect();
      maxDist = rect.width / 2 - srect.width / 2;
    }
    recalc();
    const onResize = () => recalc();
    window.addEventListener("resize", onResize);

    function setStickFromDelta(dx, dy) {
      const d = Math.hypot(dx, dy);
      const s = d > maxDist ? maxDist / d : 1;
      const hx = dx * s,
        hy = dy * s;
      stick.style.transform = `translate(${hx}px, ${hy}px) translate(-50%, -50%)`;
      const nx = hx / maxDist,
        ny = hy / maxDist;
      const m = Math.hypot(nx, ny);
      if (m < DEADZONE) {
        jx = 0;
        jy = 0;
      } else {
        const mm = (m - DEADZONE) / (1 - DEADZONE);
        jx = (nx / m) * mm;
        jy = (ny / m) * mm;
      }
    }
    function resetStick() {
      stick.style.transform = "translate(-50%, -50%)";
      jx = 0;
      jy = 0;
    }

    function carForwardAngle() {
      const q = vehicle?.chassisBody?.quaternion;
      if (q?.vmult) {
        const f = q.vmult(new CANNON.Vec3(0, 0, -1));
        return Math.atan2(f.x, f.z);
      }
      const v = vehicle?.chassisBody?.velocity || { x: 0, z: -1e-6 };
      return Math.atan2(v.x, v.z);
    }
    function signedAngle(a, b) {
      let d = b - a;
      while (d > Math.PI) d -= 2 * Math.PI;
      while (d < -Math.PI) d += 2 * Math.PI;
      return d;
    }

    function tick() {
      let desX = INVERT_X ? -jx : jx;
      let desZ = INVERT_Y ? -jy : jy;
      const mag = Math.hypot(desX, desZ);
      const using = active && mag > 1e-4;

      if (using) {
        const desired = Math.atan2(desX, desZ);
        const current = carForwardAngle();
        const dAng = signedAngle(current, desired);
        const steerTarget = clamp(
          dAng * STEER_GAIN,
          -maxSteerVal,
          +maxSteerVal
        );
        const forward = Math.cos(dAng) >= 0;
        const forceTarget = forward
          ? mag * maxForce
          : -mag * (maxForce * REVERSE_FACTOR);
        steerSm = lerp(steerSm, steerTarget, LERP);
        forceSm = lerp(forceSm, forceTarget, LERP);
        vehicle.setSteeringValue(steerSm, 0);
        vehicle.setSteeringValue(steerSm, 1);
        vehicle.setWheelForce(forceSm, 0);
        vehicle.setWheelForce(forceSm, 1);
      } else if (braking) {
        const now = performance.now();
        const v = vehicle.chassisBody.velocity;
        const speed = v.length();
        if (speed < 0.05 || now > brakeUntil) {
          vehicle.setWheelForce(0, 0);
          vehicle.setWheelForce(0, 1);
          braking = false;
        } else {
          const movingForward = v.z < -0.1 || v.x < -0.1;
          const bf = movingForward ? -brakeForce : brakeForce;
          vehicle.setSteeringValue(0, 0);
          vehicle.setSteeringValue(0, 1);
          vehicle.setWheelForce(bf, 0);
          vehicle.setWheelForce(bf, 1);
        }
      }
      rafId = requestAnimationFrame(tick);
    }
    rafId = requestAnimationFrame(tick);

    // Input plumbing (Pointer Events + Touch fallback)
    const supportsPointer = "onpointerdown" in window;
    const listeners = [];
    const add = (el, type, fn, opt) => {
      el.addEventListener(type, fn, opt);
      listeners.push([el, type, fn, opt]);
    };

    const down = (x, y, id = 1) => {
      if (active && id !== activePointerId) return;
      active = true;
      activePointerId = id;
      braking = false;
      try {
        joy.setPointerCapture?.(id);
      } catch {}
      setStickFromDelta(x - cx, y - cy);
    };
    const move = (x, y, id = 1) => {
      if (!active || id !== activePointerId) return;
      setStickFromDelta(x - cx, y - cy);
    };
    const up = (id = 1) => {
      if (id !== activePointerId) return;
      try {
        joy.releasePointerCapture?.(id);
      } catch {}
      active = false;
      activePointerId = null;
      resetStick();
      braking = true;
      brakeUntil = performance.now() + 400;
    };

    if (supportsPointer) {
      add(
        joy,
        "pointerdown",
        (e) => {
          e.preventDefault();
          down(e.clientX, e.clientY, e.pointerId);
        },
        { passive: false }
      );
      add(
        joy,
        "pointermove",
        (e) => {
          e.preventDefault();
          move(e.clientX, e.clientY, e.pointerId);
        },
        { passive: false }
      );
      add(
        document,
        "pointermove",
        (e) => {
          e.preventDefault();
          move(e.clientX, e.clientY, e.pointerId);
        },
        { passive: false }
      );
      add(
        document,
        "pointerup",
        (e) => {
          e.preventDefault();
          up(e.pointerId);
        },
        { passive: false }
      );
      add(
        document,
        "pointercancel",
        (e) => {
          e.preventDefault();
          up(e.pointerId);
        },
        { passive: false }
      );
    } else {
      const tp = (e) => e.touches?.[0] || e.changedTouches?.[0];
      add(
        joy,
        "touchstart",
        (e) => {
          e.preventDefault();
          const t = tp(e);
          if (t) down(t.clientX, t.clientY, 1);
        },
        { passive: false }
      );
      add(
        document,
        "touchmove",
        (e) => {
          e.preventDefault();
          const t = tp(e);
          if (t) move(t.clientX, t.clientY, 1);
        },
        { passive: false }
      );
      add(
        document,
        "touchend",
        (e) => {
          e.preventDefault();
          up(1);
        },
        { passive: false }
      );
      add(
        document,
        "touchcancel",
        (e) => {
          e.preventDefault();
          up(1);
        },
        { passive: false }
      );
    }

    destroyJoystick = () => {
      listeners.forEach(([el, t, fn, opt]) =>
        el.removeEventListener(t, fn, opt)
      );
      window.removeEventListener("resize", onResize);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }

  // unified cleanup
  function destroy() {
    document.removeEventListener("keydown", onKeyDown);
    document.removeEventListener("keyup", onKeyUp);
    destroyJoystick();
  }
  return { destroy };
}
